import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/shadcn/dialog';
import { Button } from '../../ui/shadcn/button';
import { Label } from '../../ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/shadcn/select';
import { AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import monitoringService, { DrillDownItem } from '../../../services/monitoring';
import { STATUS_LABELS } from '../../../constants/statusLabels';
import { colors, spacing, borderRadius, typography } from '../../../styles/design-tokens';

interface Props {
  open: boolean;
  onClose: () => void;
  items: DrillDownItem[]; // 같은 menu 만 입력됨
  menu: string;
  menuLabel: string;
  onSuccess: () => void;
}

const BulkStatusDialog: React.FC<Props> = ({ open, onClose, items, menu, menuLabel, onSuccess }) => {
  const [newStatus, setNewStatus] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const menuMap = STATUS_LABELS[menu] || {};
  // 현재 선택된 항목들의 상태는 제외 (선택한 것과 동일 상태로 변경 불가 안내)
  const currentStatuses = new Set(items.map((i) => i.status));
  const options = Object.entries(menuMap).filter(([code]) => !/^[가-힣]/.test(code)); // 영문 코드만

  const handleConfirm = async () => {
    if (!newStatus) { setError('변경할 상태를 선택하세요.'); return; }
    setSaving(true);
    setError('');
    try {
      const ids = items.map((i) => i.id);
      await monitoringService.bulkUpdateStatus({ menu, ids, newStatus });
      toast.success(`${items.length}건 상태 변경 완료`);
      onSuccess();
      onClose();
      setNewStatus('');
    } catch (e: any) {
      setError(e?.message || '상태 변경 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md sm:max-w-md" style={{ maxHeight: '85vh' }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: typography.heading.h3.fontSize, color: colors.text.primary }}>
            {menuLabel} {items.length}건 일괄 상태 변경
          </DialogTitle>
          <DialogDescription>
            선택한 항목의 상태를 한 번에 변경합니다.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: spacing.sm,
            padding: spacing.md, background: 'var(--ws-red-bg)',
            border: `1px solid ${colors.destructive}`, color: colors.destructive,
            borderRadius: borderRadius.md, fontSize: 13,
          }}>
            <AlertCircle size={16} />{error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div>
            <Label style={{ fontSize: 13, fontWeight: 500, color: colors.text.secondary, marginBottom: 6, display: 'block' }}>
              변경할 상태
            </Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="상태를 선택하세요" />
              </SelectTrigger>
              <SelectContent>
                {options.map(([code, label]) => (
                  <SelectItem key={code} value={code}>{label}{currentStatuses.has(code) && <span style={{ color: colors.text.muted, marginLeft: 6, fontSize: 11 }}>(현재 일부 항목)</span>}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label style={{ fontSize: 13, fontWeight: 500, color: colors.text.secondary, marginBottom: 6, display: 'block' }}>
              대상 {items.length}건
            </Label>
            <div style={{
              maxHeight: 180, overflowY: 'auto',
              border: `1px solid ${colors.border.light}`, borderRadius: borderRadius.md,
              padding: spacing.sm, background: colors.background.page, fontSize: 12,
            }}>
              {items.map((it) => (
                <div key={it.id} style={{ padding: '4px 0', color: colors.text.secondary, borderBottom: `1px solid ${colors.border.light}` }}>
                  · <strong>{it.title}</strong>
                  {it.customer_name && <> · {it.customer_name}</>}
                  <span style={{ color: colors.text.muted, marginLeft: 6 }}>({menuMap[it.status] || it.status})</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>취소</Button>
          <Button onClick={handleConfirm} disabled={saving || !newStatus} style={{ background: colors.primary, color: 'var(--ws-surface)' }}>
            {saving ? (<><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />처리 중...</>) : '변경 적용'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStatusDialog;
